import { useState, useEffect, useCallback } from 'react';
import { useSwrTick } from '@/lib/swrRefresh';
import axios from 'axios';
import { toast } from 'sonner';
import { DollarSign, TrendingUp, TrendingDown, ScrollText, ArrowUpDown, X, Target, Pencil, AlertTriangle, Share2, FileSpreadsheet, Trophy, ArrowLeft, ArrowRight, Sparkles, GitCompare, BellRing, Calculator, Briefcase } from 'lucide-react';
import usePriceStream from '@/hooks/usePriceStream';
import { computeRealYield } from '@/lib/realYield';
import LivePriceIndicator from '@/components/LivePriceIndicator';
import ExDivAlertSettings from '@/components/ExDivAlertSettings';
import PortfolioActionLink from '@/components/PortfolioActionLink';
import DailyStockTracker from '@/components/DailyStockTracker';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import InfoTip from './InfoTip';
import CapitalTrackerCard from './CapitalTrackerCard';
import NetCapitalGainModal from './NetCapitalGainModal';
import PortfolioCharts from './PortfolioCharts';
import IncomeConcentrationBanner from './IncomeConcentrationBanner';
import PortfolioSelector, { useActivePortfolioId } from './PortfolioSelector';
import { useIsReadOnlyAllView } from '@/lib/useIsReadOnlyAllView';
import AllViewReadOnlyBanner from './AllViewReadOnlyBanner';
import MacroYieldStrip from './MacroYieldStrip';
import useCpiYoy from '@/hooks/useCpiYoy';
import { advancePortfolioLoadStage, startPortfolioLoadStaircase } from '@/lib/portfolioLoadOrder';
import { dedupedGet } from '@/lib/dedupedGet';
import BenchmarkSparkline from '@/components/BenchmarkSparkline';
import BenchmarkBeatModal from '@/components/BenchmarkBeatModal';

// Definitions for the four dividend-friendly benchmarks displayed in
// the portfolio Benchmark Comparison card. Used by InfoTip hover/tap
// popovers so users can see what each ETF tracks without leaving the
// portfolio page.
const BENCHMARK_INFO = {
  SCHD: {
    term: 'SCHD · Schwab U.S. Dividend Equity ETF',
    def: 'Tracks the Dow Jones U.S. Dividend 100 Index — 100 U.S. stocks screened for 10+ years of dividend payments, strong cash flow, return on equity, yield, and 5-year dividend growth. Widely considered the benchmark for dividend-growth quality investing.',
  },
  VYM: {
    term: 'VYM · Vanguard High Dividend Yield ETF',
    def: 'Tracks the FTSE High Dividend Yield Index — large-cap U.S. stocks with higher-than-average forecasted dividend yields (excludes REITs). Broad exposure with ~450 holdings; the go-to yardstick for high-yield diversified investing.',
  },
  HDV: {
    term: 'HDV · iShares Core High Dividend ETF',
    def: 'Tracks the Morningstar Dividend Yield Focus Index — 75 high-quality U.S. stocks with strong economic moats, financial health, and sustainable high dividends. Concentrated around energy, healthcare, and consumer staples.',
  },
  NOBL: {
    term: 'NOBL · ProShares S&P 500 Dividend Aristocrats ETF',
    def: 'Tracks the S&P 500 Dividend Aristocrats Index — companies in the S&P 500 that have increased their dividend for at least 25 consecutive years. Equal-weighted across ~65 blue-chip dividend growers.',
  },
  SPY: {
    term: 'SPY · SPDR S&P 500 ETF Trust',
    def: 'Tracks the S&P 500 Index — 500 of the largest U.S. companies by market cap, cap-weighted across all sectors. The most widely cited benchmark for U.S. large-cap equity performance.',
  },
  QQQ: {
    term: 'QQQ · Invesco QQQ Trust',
    def: 'Tracks the Nasdaq-100 Index — the 100 largest non-financial companies on the Nasdaq. Heavily weighted toward technology and growth names; the standard proxy for U.S. mega-cap growth.',
  },
  DIA: {
    term: 'DIA · SPDR Dow Jones Industrial Average ETF',
    def: 'Tracks the Dow Jones Industrial Average — 30 large, blue-chip U.S. companies, price-weighted. The classic "Dow" benchmark for established American industry leaders.',
  },
  IWM: {
    term: 'IWM · iShares Russell 2000 ETF',
    def: 'Tracks the Russell 2000 Index — approximately 2,000 small-cap U.S. companies. The go-to benchmark for U.S. small-cap equity performance and broader economic breadth.',
  },
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
  { key: '1m', label: '1M' },
  { key: '3m', label: '3M' },
  { key: '6m', label: '6M' },
  { key: '1y', label: '1Y' },
  { key: '5y', label: '5Y' },
  { key: 'max', label: 'MAX' },
];

function formatValue(val) {
  if (val >= 1000000) return `$${(val / 1000000).toFixed(2)}M`;
  if (val >= 1000) return `$${(val / 1000).toFixed(2)}K`;
  return `$${val.toFixed(2)}`;
}


function IncomeGoalBar({ goal, monthlyIncome, portfolioYield, onEditClick }) {
  const g = Number(goal?.monthly_goal || 0);
  const growth = Number(goal?.growth_pct || 8);
  // portfolioYield is a percent (e.g. 30 for 30%) — same shape as
  // `data.portfolio_yield` from /api/portfolio/summary.
  const yld = Number(portfolioYield || 0);

  if (g <= 0) {
    return (
      <div className="mt-3 flex items-center justify-between gap-3 flex-wrap" data-testid="income-goal-bar">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Set a monthly dividend income goal to track your progress.</span>
        </div>
        {onEditClick && (
          <button
            onClick={onEditClick}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold bg-violet-100 dark:bg-violet-950/50 text-violet-700 dark:text-violet-300 hover:bg-violet-200 dark:hover:bg-violet-950/70 active:scale-95 transition-all shadow-sm"
            data-testid="income-goal-set-btn"
          >
            <Target className="w-4 h-4" />
            <span>Set Goal</span>
          </button>
        )}
      </div>
    );
  }

  const pct = Math.min(100, (monthlyIncome / g) * 100);
  // DRIP-aware projection: each year DRIP reinvests dividends → share
  // count grows by ~portfolio_yield, then per-share dividend grows by
  // `growth`. Effective annual income factor = (1+y) × (1+g).
  // This matches the Portfolio Dividend Calculator's DRIP-On math and
  // is why a -3% grower at 30% yield can still hit a goal (effective
  // ≈ +26%/yr). Falls back to plain growth if portfolio yield is 0.
  const annualGrowth = (1 + (yld / 100)) * (1 + (growth / 100));
  // Years until monthlyIncome * annualGrowth^years >= g.
  // Round 86 (Feb 2026) — Allow negative growth. With DRIP factored
  // in, "unreachable" only triggers when effective factor ≤ 1 (i.e.
  // dividend decay outpaces the yield-driven share accumulation).
  let projected = null;
  if (monthlyIncome >= g) {
    projected = 'Goal reached 🎉';
  } else if (monthlyIncome > 0 && annualGrowth > 1) {
    const years = Math.log(g / monthlyIncome) / Math.log(annualGrowth);
    const target = new Date();
    target.setFullYear(target.getFullYear() + Math.ceil(years));
    projected = target.toLocaleString('en-US', { month: 'short', year: 'numeric' });
  } else if (monthlyIncome > 0) {
    projected = 'Goal unreachable at current rate';
  }

  return (
    <div className="mt-2" data-testid="income-goal-bar">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1.5">
        {onEditClick ? (
          <button
            onClick={onEditClick}
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            data-testid="income-goal-edit-btn"
            title="Edit goal or growth rate"
          >
            <Pencil className="w-3.5 h-3.5 text-amber-500" /> edit
          </button>
        ) : (
          <span aria-hidden className="text-xs invisible">edit</span>
        )}
        <span className="text-xs font-mono ml-auto" data-testid="income-goal-progress-text">
          <span className="font-bold text-green-700 dark:text-green-400">${monthlyIncome.toFixed(2)}</span>
          <span className="text-muted-foreground"> / </span>
          <span className="font-bold text-foreground">${g.toFixed(0)}/mo</span>
          <span className="text-violet-600 dark:text-violet-400 ml-1.5 font-bold">({pct.toFixed(1)}%)</span>
        </span>
      </div>
      <div className="w-full h-2 rounded-full bg-muted overflow-hidden" data-testid="income-goal-progress-bar">
        <div
          className="h-full bg-gradient-to-r from-violet-500 to-emerald-500 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      {projected && (
        <p className="text-[11px] text-muted-foreground mt-1.5 text-right" title={yld > 0 ? `DRIP-aware projection: effective annual growth ≈ (1 + ${yld.toFixed(2)}% yield) × (1 + ${growth}% div growth) − 1 = ${((annualGrowth - 1) * 100).toFixed(2)}%/yr` : 'Projection uses dividend growth only (no DRIP yield available yet).'}>
          Projected at {growth}% growth{yld > 0 ? ` + ${yld.toFixed(1)}% DRIP` : ''}: <span className="font-semibold text-foreground" data-testid="income-goal-projected">{projected}</span>
        </p>
      )}
    </div>
  );
}

function GoalModal({ open, initial, onClose, onSaved }) {
  const [goal, setGoal] = useState('');
  const [growth, setGrowth] = useState('8');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open) {
      setGoal(String(initial?.monthly_goal || ''));
      setGrowth(String(initial?.growth_pct || 8));
      setErr('');
    }
  }, [open, initial]);

  const save = async () => {
    const g = Number(goal);
    const gr = Number(growth);
    if (!(g >= 0)) { setErr('Enter a valid goal amount'); return; }
    if (gr < -25 || gr > 25) { setErr('Growth must be -25% to 25%'); return; }
    setBusy(true); setErr('');
    try {
      const { data } = await axios.put(`${API}/portfolio/income-goal`, { monthly_goal: g, growth_pct: gr }, { withCredentials: true });
      onSaved(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to save');
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm p-0 overflow-hidden" data-testid="income-goal-modal">
        <div className="border-b px-5 py-3 flex items-center gap-2">
          <Target className="w-5 h-5 text-violet-500" />
          <h3 className="text-base font-bold">Monthly Income Goal</h3>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1">Target monthly dividend income ($)</label>
            <input
              type="number" min="0" step="any"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              className="w-full px-3 py-2 text-sm font-mono rounded border bg-background"
              placeholder="500"
              data-testid="income-goal-input"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1">Assumed annual dividend-income growth (%)</label>
            <input
              type="number" min="-25" max="25" step="0.1"
              value={growth}
              onChange={(e) => setGrowth(e.target.value)}
              className="w-full px-3 py-2 text-sm font-mono rounded border bg-background"
              placeholder="8"
              data-testid="income-goal-growth-input"
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              Typical dividend portfolios grow income 5-10%/yr (dividend raises + reinvestment). 8% is a reasonable default.
            </p>
            {/* Round 86 (Feb 2026) — Warn high-yield holders that
                the optimistic default rarely applies, and tell them
                negative growth is now allowed (so they can model
                distribution decay realistically). */}
            <div
              className="mt-2 rounded-md border border-amber-300/70 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-[11px] text-amber-800 dark:text-amber-200 leading-snug"
              data-testid="income-goal-high-yield-warning"
            >
              <strong>Holding high-yield ETFs?</strong> Distributions on funds like MSTY,
              ULTY, QQQY, JEPY etc. typically <em>decline</em> over time as their
              option premium decays. Set growth to <strong>-1% to -5%</strong> to model
              that distribution erosion, or leave it at <strong>0%</strong> for a
              steady-distribution assumption.
            </div>
          </div>
          {err && <p className="text-xs text-red-500" data-testid="income-goal-error">{err}</p>}
        </div>
        <div className="border-t px-5 py-3 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded text-xs font-semibold text-muted-foreground hover:text-foreground"
            data-testid="income-goal-cancel-btn"
          >Cancel</button>
          <button
            onClick={save}
            disabled={busy}
            className="px-3 py-1.5 rounded text-xs font-semibold text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-60"
            data-testid="income-goal-save-btn"
          >{busy ? 'Saving...' : 'Save Goal'}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function SummaryCard({ isDark, onWhatIfClick, onStockClick }) {
  const [data, setData] = useState(null);
  const [bump, setBump] = useState(0);
  // Round 96n (Jul 3 2026) — CPI YoY for inline "real" chips on the
  // Portfolio Yield + Yield on Cost KPI cards. Reuses the shared
  // singleton-cached hook (same source of truth as StockTable, DRIP
  // calcs, and the Blend Compare card).
  const cpiYoy = useCpiYoy();
  // Round 93h (Jun 2026) — fetch the heatmap's projected-annual total
  // so we can warn the user when the cache-derived "Annual Dividends"
  // number drifts from what the calendar projection actually shows.
  // Drift > 5% surfaces a small "⚠ projected: $X/yr" caption under
  // the headline number on the Annual Dividends tile.
  const [heatmapProjectedAnnual, setHeatmapProjectedAnnual] = useState(null);
  const [goal, setGoal] = useState({ monthly_goal: 0, growth_pct: 8 });
  const [editingGoal, setEditingGoal] = useState(false);
  // Round 84k Phase C scaffolding (Feb 15, 2026) — subscribe to live
  // prices for the user's owned tickers. The hook is cheap when the
  // backend WebSocket plan is dormant; it just emits heartbeats every
  // 15s and shows "Polling" on the indicator. Flips to "Live" with
  // pulsing green dot once `MASSIVE_WEBSOCKET_ENABLED=true`.
  const [ownedTickers, setOwnedTickers] = useState([]);
  useEffect(() => {
    axios.get(`${API}/portfolio/owned-tickers`, { withCredentials: true })
      .then((res) => setOwnedTickers(Array.isArray(res.data) ? res.data : []))
      .catch(() => setOwnedTickers([]));
  }, [bump]);
  // eslint-disable-next-line no-unused-vars
  const { status: streamStatus, prices: livePrices } = usePriceStream(ownedTickers);

  // Round 86c (May 2026) — Auto-refresh the My Portfolio summary
  // whenever live prices tick. Previously the totals at the TOP of
  // the page went stale while the stock-table rows at the bottom
  // updated live — users had to refresh the page to see Net Capital
  // Gain / Total Value / Net Worth keep up with price moves.
  //
  // Round 93e (Jun 2026) — debounce shortened from 5s → 3s so the
  // summary feels closer to real-time. A market-open burst still
  // collapses to one backend hit per 3s window. Bump triggers the
  // existing summary fetch at line ~270. Also dispatches a
  // `live-prices-tick` window event so any other price-sensitive card
  // on the Portfolio page (PortfolioCharts — Portfolio Value Over
  // Time, Sector Allocation) can hook in and self-refresh without
  // re-implementing the debounce.
  useEffect(() => {
    if (!livePrices || Object.keys(livePrices).length === 0) return;
    const id = setTimeout(() => {
      setBump((b) => b + 1);
      try { window.dispatchEvent(new Event('live-prices-tick')); } catch { /* SSR safety */ }
    }, 3000);
    return () => clearTimeout(id);
  }, [livePrices]);

  // Round 92o++++++++N++++++++++ (May 2026) — Plus multi-portfolio.
  // Read the active portfolio selection from the global store so the
  // summary, owned-tickers list, and any other portfolio-id-aware
  // endpoint can be re-queried whenever the user switches the
  // dropdown above this card. Free users always see `activeId=null`
  // (`isPlus=false`) and the experience is identical to today.
  const { activeId: activePortfolioId, loaded: portfolioStoreLoaded } = useActivePortfolioId();

  const swrTick = useSwrTick('/portfolio/summary');
  useEffect(() => {
    // Wait for the portfolio store so we never fire a wasted "All" request
    // ahead of the correctly-scoped one (the prefetch in AuthContext already
    // has the scoped headline in flight).
    if (!portfolioStoreLoaded) return;
    const params = activePortfolioId ? { portfolio_id: activePortfolioId } : {};
    // Round P124c (Feb 2026) — Kick off the time-based load
    // staircase. Stage 2 fires at t+120ms, 3 at +240ms, 4 at
    // +480ms, 5 at +900ms. Each subscribing card gates its fetch
    // on the shared stage counter. Idempotent — safe on remounts
    // and portfolio switches (clears + reschedules timers).
    // Sep 2026 — top card reads the ALWAYS-LIVE `/portfolio/headline` (2-3 ms
    // server-side, no snapshot). The load staircase for the rest of the page
    // is released only AFTER the headline has painted, so nothing queues in
    // front of it in the browser's 6-connection lane.
    dedupedGet(`${API}/portfolio/headline`, { params, withCredentials: true })
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => startPortfolioLoadStaircase());
    axios.get(`${API}/portfolio/income-goal`, { withCredentials: true })
      .then(res => setGoal(res.data))
      .catch(() => {});
    axios.get(`${API}/portfolio/income-heatmap`, {
      params: { ...params, months: 12, include_watchlist: false },
      withCredentials: true,
    })
      .then(res => setHeatmapProjectedAnnual(Number(res.data?.total_annual) || 0))
      .catch(() => setHeatmapProjectedAnnual(null));
  }, [bump, activePortfolioId, swrTick, portfolioStoreLoaded]);

  // Refresh whenever cash or positions change
  useEffect(() => {
    const h = () => setBump((b) => b + 1);
    window.addEventListener('cash-changed', h);
    window.addEventListener('portfolio-changed', h);
    return () => {
      window.removeEventListener('cash-changed', h);
      window.removeEventListener('portfolio-changed', h);
    };
  }, []);

  const [showTransactions, setShowTransactions] = useState(false);
  // Round 92o++++++++N++++++++++ (May 2026) — modal that explains
  // the "Net Capital Gain" tile when the user clicks it. Read-only,
  // computes from the same `data` payload the tile already uses so
  // the modal can never desync from the headline number above it.
  const [ncgModalOpen, setNcgModalOpen] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  // Round 91 — Yes/No confirm modal before kicking off the
  // Google / Excel Sheet download. Per user request: clicking the
  // button (or the Tool Box tile) opens a small modal; only "Yes"
  // actually downloads. "No", the red ❌ close, and a click outside
  // the modal all cancel.
  const [confirmDownloadOpen, setConfirmDownloadOpen] = useState(false);
  // Round 95k (Jun 2026) — Ex-Div Alert settings panel, same control
  // as the Dividend Calendar header. Button sits left of the Tool Box
  // launcher; panel discloses below the fidelity strip.
  const [alertSettingsOpen, setAlertSettingsOpen] = useState(false);
  // Empty-portfolio guard modal — shown when the user clicks Share or
  // Download Sheet with zero positions in their portfolio. Avoids
  // generating a useless empty share-page or empty .xlsx workbook.
  const [emptyAction, setEmptyAction] = useState(null); // 'share' | 'sheet' | null

  const isPortfolioEmpty = !data || (data.position_count || 0) === 0;

  // Round 86c (May 2026) — Tool Box relays. The Tool Box modal lives
  // on Dashboard but two of its tiles ("All Transactions", "Google /
  // Excel Sheet") need handlers that live HERE (local state +
  // `downloadSheetTemplate` closure). Wire them via window events.
  useEffect(() => {
    const openTxn = () => {
      if (isPortfolioEmpty) { setEmptyAction('share'); return; }
      setShowTransactions(true);
    };
    const downloadSheet = () => { askDownloadSheet(); };
    window.addEventListener('open-all-transactions-modal', openTxn);
    window.addEventListener('toolbox-download-sheet-template', downloadSheet);
    return () => {
      window.removeEventListener('open-all-transactions-modal', openTxn);
      window.removeEventListener('toolbox-download-sheet-template', downloadSheet);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPortfolioEmpty]);

  const openShareModal = () => {
    if (isPortfolioEmpty) { setEmptyAction('share'); return; }
    // Dashboard.js owns the SharePortfolioModal state — it listens for this
    // window event and opens the modal. Keeps this component independent.
    window.dispatchEvent(new Event('open-share-portfolio-modal'));
  };

  // Round 91 — opens the Yes/No confirm modal. Empty-portfolio guard
  // takes precedence over the confirm (same UX as before).
  const askDownloadSheet = () => {
    if (downloadingTemplate) return;
    if (isPortfolioEmpty) { setEmptyAction('sheet'); return; }
    setConfirmDownloadOpen(true);
  };

  const downloadSheetTemplate = async () => {
    if (downloadingTemplate) return;
    if (isPortfolioEmpty) { setEmptyAction('sheet'); return; }
    setDownloadingTemplate(true);
    try {
      const res = await axios.get(`${API}/portfolio/download-template`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.download = `dividrip_template_${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Template downloaded', {
        description: 'Upload it to Google Drive and share the /copy link with friends.',
      });
    } catch {
      toast.error("Couldn't download the template", {
        description: 'Please try again in a moment.',
      });
    } finally {
      setDownloadingTemplate(false);
    }
  };

  if (!data) return null;

  const posCount = data.position_count || 0;
  const posLabel = posCount === 1 ? '1 position' : `${posCount} positions`;
  // positions_value may not exist in older responses (before cash feature); fall back
  // to total_value minus cash_balance for backwards compatibility.
  const stockValue = Number(
    data.positions_value != null
      ? data.positions_value
      : (data.total_value || 0) - (data.cash_balance || 0)
  ) || 0;

  return (
    <div>
      <div className="rounded-lg border bg-card p-4 mb-3" data-testid="portfolio-summary-card">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
          <DollarSign className="w-4 h-4 text-primary" />
        </div>
        <h2 className="text-base font-bold">My Portfolio</h2>
        <LivePriceIndicator status={streamStatus} />
        <button
          type="button"
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[13px] font-semibold border-2 border-blue-600 dark:border-blue-400 bg-transparent text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/40 transition-colors"
          onClick={() => {
            // Scroll to the action bar so the user sees the full row of
            // 7 buttons (News, Calendar, DRIP, Rebalancer, What If?,
            // Compare, Export CSV, Import CSV) AND the first portfolio
            // rows below. We use scrollIntoView + scroll-margin-top
            // (set on the target element via inline style fallback) so
            // browser handles the offset natively and we don't fight a
            // sticky header during a smooth scroll.
            // Round 92o++++++++b (Feb 2026) — the original
            // `portfolio-action-bar` element was retired in the May
            // 2026 action-bar cleanup (icons moved into their
            // context-relevant cards), which silently broke this
            // scroll. Retarget at the PortfolioView root
            // (`portfolio-stocks-section`) — that's where the
            // stock-bar rows actually live.
            const el = document.getElementById('portfolio-stocks-section');
            if (el) {
              el.style.scrollMarginTop = '88px';
              el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }}
          data-testid="positions-scroll-link"
          title="Scroll to positions"
        >
          <Briefcase className="w-[15px] h-[15px]" />
          {posLabel}
        </button>

        {/* Share + Download Sheet — next to the title on desktop,
            wrap below it on phones (parent has flex-wrap). */}
        <div className="flex items-center gap-1.5 ml-auto flex-wrap">
          <button
            onClick={openShareModal}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[13px] font-semibold border border-blue-300/60 dark:border-blue-800/60 bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-950/60 transition-colors"
            data-testid="title-share-portfolio-btn"
            title="Share your portfolio as an anonymised public link"
          >
            <Share2 className="w-[15px] h-[15px]" />
            My Share Page
          </button>
          <button
            onClick={askDownloadSheet}
            disabled={downloadingTemplate}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[13px] font-semibold border border-emerald-300/60 dark:border-emerald-800/60 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-950/60 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="title-download-template-btn"
            title="Download an editable Google Sheets / Excel dividend tracker pre-filled with your portfolio"
          >
            <FileSpreadsheet className="w-[15px] h-[15px]" />
            {downloadingTemplate ? 'Building…' : 'Google / Excel Sheet'}
          </button>
          {/* All Transactions — moved to end of action group so it wraps cleanly. */}
          <button
            onClick={() => setShowTransactions(true)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[13px] font-semibold border border-violet-300/60 dark:border-violet-800/60 bg-violet-50 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-950/60 transition-colors"
            data-testid="all-transactions-link"
            title="View all portfolio transactions"
          >
            <ScrollText className="w-[15px] h-[15px]" />
            All Transactions
          </button>
          {/* What If? — May 2026: relocated from the page-wide action
              bar into this title row, immediately after "All
              Transactions", per user request. */}
          {onWhatIfClick && (
            <PortfolioActionLink
              icon={Sparkles}
              label="What If?"
              onClick={onWhatIfClick}
              tone="text-violet-500 dark:text-violet-400"
              testId="portfolio-whatif-btn"
              title="Run scenarios on top of your portfolio without touching your real positions"
              sizeBoost={1}
              bordered
            />
          )}
        </div>
      </div>
      {/* 6-up grid (2 cols on mobile, 3 on md, 6 on lg+). Net
          Capital Gain joined the lineup so we drop to text-lg with
          tight labels to keep everything on one row at desktop width. */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="text-center">
          <p className="text-[12px] text-muted-foreground mb-1">Total Value</p>
          {(() => {
            // Cash is per-portfolio (the backend scopes the ledger), so
            // total_value already equals this view's positions + cash.
            const displayTotal = Number(data.total_value || 0);
            return (
              <p className="text-lg font-bold font-mono" data-testid="summary-total-value">${displayTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            );
          })()}
          <p className="text-[11px] text-muted-foreground" data-testid="summary-total-value-sublabel">
            cash + positions
          </p>
        </div>
        <div className="text-center">
          <p className="text-[12px] text-muted-foreground mb-1">Total Stock Value</p>
          <p className="text-lg font-bold font-mono" data-testid="summary-stock-value">${stockValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          <p className="text-[11px] text-muted-foreground">positions only</p>
        </div>
        <div className="text-center" title="Yield on Cost — total annual dividends ÷ total cost basis you originally paid. Shows how hard your initial money is working today as companies raise their dividends.">
          <p className="text-[12px] text-muted-foreground mb-1">Yield on Cost</p>
          <p className="text-lg font-bold font-mono text-violet-600 dark:text-violet-400" data-testid="summary-yoc">{Number(data.yield_on_cost ?? 0).toFixed(2)}%</p>
          <p className="text-[11px] text-muted-foreground">on cost basis</p>
          {/* Round 96n (Jul 3 2026) — Real Yield-on-Cost chip.
              Subtracts current CPI YoY so users can see whether the
              income their original dollars generate is beating
              inflation. Only renders when CPI has loaded. */}
          {typeof cpiYoy === 'number' && Number(data.yield_on_cost ?? 0) > 0 && (() => {
            const real = computeRealYield(Number(data.yield_on_cost), cpiYoy);
            if (real == null) return null;
            const cls = real < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400';
            return (
              <p
                className={`text-[10px] font-semibold mt-0.5 ${cls}`}
                title={`Real YoC (Fisher-exact) = (1 + ${Number(data.yield_on_cost).toFixed(2)}%) / (1 + CPI ${cpiYoy.toFixed(2)}%) − 1 = ${real.toFixed(2)}%. This is whether your original investment's income is beating inflation right now.`}
                data-testid="summary-yoc-real"
              >
                {real >= 0 ? '+' : ''}{real.toFixed(2)}% real
              </p>
            );
          })()}
        </div>
        <div className="text-center">
          <p className="text-[12px] text-muted-foreground mb-1">Annual Dividends</p>
          <p className="text-lg font-bold font-mono text-green-700 dark:text-green-400" data-testid="summary-annual-divs">${Number(data.annual_dividends || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          <p className="text-[11px] text-muted-foreground">${Number(data.monthly_dividends || 0).toFixed(2)}/mo</p>
          {/* Round 93h (Jun 2026) — Drift warning. If the 12-month
              heatmap's projected total differs from the rate-derived
              annual_dividends by >5%, surface the projection so the
              user can spot a stale dividend_rate immediately (e.g.
              EGGY weekly with sparse TTM coverage, a suspended
              payer, etc.). Hidden when in sync. */}
          {(() => {
            const annual = Number(data.annual_dividends) || 0;
            const projected = Number(heatmapProjectedAnnual);
            if (!Number.isFinite(projected) || projected <= 0 || annual <= 0) return null;
            const diffPct = Math.abs(projected - annual) / annual * 100;
            if (diffPct <= 5) return null;
            const higher = projected > annual;
            return (
              <p
                className="text-[10px] mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-medium bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border border-amber-300/60 dark:border-amber-800/60"
                data-testid="annual-dividends-drift-badge"
                title={`The 12-month income heatmap projects $${projected.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} from this portfolio's next payments. The headline number above is rate × shares from the cache; a drift of ${diffPct.toFixed(1)}% usually means a recent dividend change or a stale rate. Will reconcile on the next overlay cycle.`}
              >
                ⚠ projected: ${projected.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/yr ({higher ? '+' : '−'}{diffPct.toFixed(1)}%)
              </p>
            );
          })()}
        </div>
        <div className="text-center">
          <p className="text-[12px] text-muted-foreground mb-1">Portfolio Yield</p>
          <p className="text-lg font-bold font-mono text-blue-700 dark:text-blue-400" data-testid="summary-yield">{data.portfolio_yield}%</p>
          <p className="text-[11px] text-muted-foreground">on invested</p>
          {/* Round 96n (Jul 3 2026) — Real Portfolio Yield chip.
              Complements the existing MacroYieldStrip by putting the
              inflation-adjusted figure right on the KPI tile itself
              (so it shows up in shared screenshots too). */}
          {typeof cpiYoy === 'number' && Number(data.portfolio_yield ?? 0) > 0 && (() => {
            const real = computeRealYield(Number(data.portfolio_yield), cpiYoy);
            if (real == null) return null;
            const cls = real < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400';
            return (
              <p
                className={`text-[10px] font-semibold mt-0.5 ${cls}`}
                title={`Real Portfolio Yield (Fisher-exact) = (1 + ${Number(data.portfolio_yield).toFixed(2)}%) / (1 + CPI ${cpiYoy.toFixed(2)}%) − 1 = ${real.toFixed(2)}%. Positive means your dividend income is outrunning inflation; negative means you're losing purchasing power.`}
                data-testid="summary-yield-real"
              >
                {real >= 0 ? '+' : ''}{real.toFixed(2)}% real
              </p>
            );
          })()}
        </div>
        {(() => {
          // Net Capital Gain — how much your total wealth (positions +
          // cash) has grown vs. the cash you actually put in from your
          // own pocket. This mirrors Robinhood's "All-Time" pill and
          // every consumer broker's headline number.
          //
          // Formula:  Net Capital Gain = Total Value − Net Contributions
          //
          // Feb 2026 (Round P111a) — Removed the "− Dividends Collected"
          // term that made the tile MORE negative the more dividends you
          // received. Since dividends are already part of Total Value
          // (either as cash inside the portfolio or as new DRIP shares),
          // subtracting them again double-counts them the wrong way and
          // hides your true return.
          //
          // Round P108u (Feb 2026) — CSV-import fallback. If the
          // portfolio has positions but no tracked contributions/
          // dividends (typical for CSV imports without recorded cash
          // deposits), fall back to plain Unrealized G/L
          // (positions_value − total_cost_basis) so this tile matches
          // what Tax Lots, Tax Account Audit, and Year-End all show.
          const totalValue = Number(data.total_value) || 0;
          const netContributions = Number(data.net_contributions) || 0;
          const dividendsCollected = Number(data.total_dividends_collected) || 0;
          const totalContributions = Number(data.total_contributions) || 0;
          const positionsValue = Number(data.positions_value) || 0;
          const totalCostBasis = Number(data.total_cost_basis) || 0;
          const positionCount = Number(data.position_count) || 0;

          const hasContribHistory = totalContributions > 0 || dividendsCollected > 0;
          const hasPositions = positionCount > 0 && totalCostBasis > 0;
          if (!hasContribHistory && !hasPositions) return null;

          const ncg = hasContribHistory
            ? totalValue - netContributions
            : positionsValue - totalCostBasis;
          const positive = ncg > 0.005;
          const negative = ncg < -0.005;
          const label = hasContribHistory ? 'Net Capital Gain' : 'Unrealized G/L';
          const subLabel = hasContribHistory
            ? 'total value − net contributions'
            : 'market value − cost basis';
          const tileTitle = hasContribHistory
            ? "Net Capital Gain = Total Value − Net Contributions. Shows how much your total wealth (positions + cash + reinvested dividends) has grown versus the out-of-pocket money you actually contributed. Same headline number your broker shows as All-Time gain. Tap for the full breakdown."
            : "Unrealized G/L = Current Market Value − Total Cost Basis. This matches the number on your Tax Lots, Tax Account Audit, and Year-End Snapshot pages. Add outside cash deposits under Capital Tracker to unlock the fuller Net Capital Gain breakdown.";
          // Round 92o++++++++N++++++++++ (May 2026) — coloured border
          // around the clickable Net Capital Gain tile so the user
          // catches portfolio sign at a glance: green = positive,
          // red = negative, grey = zero. Hover/focus tints the same
          // colour so the affordance stays consistent.
          const borderTone = positive
            ? 'border-2 border-green-400 dark:border-green-700 hover:bg-green-50/60 dark:hover:bg-green-950/30 focus-visible:ring-green-500/40'
            : negative
              ? 'border-2 border-red-400 dark:border-red-700 hover:bg-red-50/60 dark:hover:bg-red-950/30 focus-visible:ring-red-500/40'
              : 'border-2 border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100/60 dark:hover:bg-zinc-800/40 focus-visible:ring-zinc-400/40';
          return (
            <button
              type="button"
              onClick={() => setNcgModalOpen(true)}
              className={`text-center rounded-md focus-visible:outline-none focus-visible:ring-2 transition-colors px-2 py-1 cursor-pointer ${borderTone}`}
              title={tileTitle}
              data-testid="summary-net-capital-gain-button"
            >
              <p className="text-[12px] text-muted-foreground mb-1">{label}</p>
              <p
                className={`text-lg font-bold font-mono ${
                  positive
                    ? 'text-green-700 dark:text-green-400'
                    : negative
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-zinc-600 dark:text-zinc-300'
                }`}
                data-testid="summary-net-capital-gain"
              >
                {positive ? '+' : negative ? '−' : ''}${Math.abs(ncg).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p className="text-[11px] text-muted-foreground">{subLabel}</p>
              <p className="text-[10px] text-violet-500 dark:text-violet-400 mt-0.5" data-testid="summary-net-capital-gain-hint">
                Click or Tap for Breakdown
              </p>
            </button>
          );
        })()}
      </div>

      {/* Round 92o++++++++N++++++++++ (May 2026) — Net Capital Gain
          drill-down modal. Mounted at the summary-row sibling level
          so the Dialog portal renders above all the card content.
          All values come from the same `data` payload the tile uses
          above — no extra fetch, no chance of desync. */}
      <NetCapitalGainModal
        open={ncgModalOpen}
        onClose={() => setNcgModalOpen(false)}
        totalValue={Number(data?.total_value) || 0}
        netContributions={Number(data?.net_contributions) || 0}
        dividendsCollected={Number(data?.total_dividends_collected) || 0}
        totalContributions={Number(data?.total_contributions) || 0}
        totalWithdrawals={Number(data?.total_withdrawals) || 0}
        fallbackMode={
          (Number(data?.total_contributions) || 0) === 0
          && (Number(data?.total_dividends_collected) || 0) === 0
        }
        positionsValue={Number(data?.positions_value) || 0}
        totalCostBasis={Number(data?.total_cost_basis) || 0}
      />

      {/* Round 86c (May 2026) — Mobile-only Tool Box launcher row.
          The DESKTOP launcher now lives INSIDE the fidelity-strip row
          below so it shares a single line with the Forward/Last-365d
          pill per user request. Hidden on mobile here because the
          mobile launcher already sits next to the Search bar. */}

      {/* ────────────────────────────────────────────────────────────
          Round 77 — Forward vs Trailing-12 + Year-3/5 income forecast
          ────────────────────────────────────────────────────────────
          Round 86c (May 2026) — The desktop Tool Box launcher now
          shares this row, sitting flush-right while the Forward / TTM
          / projection pills stay centered. The row always renders on
          ≥sm so the Tool Box stays visible even when no fidelity
          pills are eligible.
          ──────────────────────────────────────────────────────────── */}
      {(() => {
        const fwd = Number(data.annual_dividends || 0);
        const ttm = Number(data.annual_dividends_ttm_actual || 0);
        const y3 = Number(data.annual_income_year_3 || 0);
        const y5 = Number(data.annual_income_year_5 || 0);
        const cagrCov = Number(data.cagr_coverage_pct || 0);
        const showForwardTTM = Math.abs(fwd - ttm) >= 1 && ttm > 0;
        const showProjection = cagrCov > 0 && y3 > 0 && y5 > 0 && (y3 > fwd || y5 > fwd);
        const pendingPct = ttm > 0 ? ((fwd - ttm) / ttm) * 100 : 0;
        const wc3 = Number(data.weighted_cagr_3y || 0);
        const wc5 = Number(data.weighted_cagr_5y || 0);
        // ToolBox launcher — desktop only. Always rendered on ≥sm so
        // users never lose the entry point even when no fidelity pill
        // is eligible (e.g. brand-new portfolio without a TTM gap).
        const ToolBoxBtn = (
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event('open-toolbox-modal'))}
            data-testid="toolbox-launcher-desktop"
            className="hidden sm:inline-flex shrink-0 items-center gap-2 px-3 py-1.5 rounded-lg border border-fuchsia-300/60 dark:border-amber-200/40 bg-gradient-to-r from-fuchsia-50 to-amber-50 dark:from-fuchsia-950/30 dark:to-amber-950/30 hover:from-fuchsia-100 hover:to-amber-100 dark:hover:from-fuchsia-900/40 dark:hover:to-amber-900/40 text-xs font-semibold transition-all active:scale-95"
            title="Open the Tool Box — every portfolio tool in one place"
          >
            <img src="/icons/toolbox.png" alt="" className="w-[22px] h-[22px] object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            <span className="text-fuchsia-800 dark:text-amber-100">Tool Box</span>
          </button>
        );
        // Skip the entire row on mobile when no pills are eligible —
        // the toolbox is reachable from the mobile search-bar launcher
        // anyway. Desktop always renders so the toolbox stays put.
        const anyPill = showForwardTTM || showProjection;
        return (
          <div className="mt-3 px-2 sm:px-3" data-testid="summary-fidelity-strip">
            <div className="relative flex flex-wrap items-center justify-center gap-2 text-[10px] sm:text-[11px]">
              {!anyPill && (
                /* Mobile placeholder spacer so the row doesn't render
                    completely empty when desktop hides the toolbox
                    (we render the toolbox only at sm+ via the
                    `hidden sm:inline-flex` class). */
                <span className="sm:hidden h-0" />
              )}
              {showForwardTTM && (
                <div
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-dashed border-muted-foreground/30 bg-muted/20"
                  title="Forward annual = today's per-payment × payments-per-year. Trailing-12 actual = sum of dividends actually paid in the last 365 days. The gap means a recent raise hasn't fully rotated through yet — within ~12 months they'll converge."
                  data-testid="summary-fwd-ttm-pill"
                >
                  <span className="text-muted-foreground">Forward</span>
                  <span className="font-mono font-bold text-green-700 dark:text-green-400">${fwd.toFixed(2)}</span>
                  <span className="text-muted-foreground/70">·</span>
                  <span className="text-muted-foreground">Last 365d actual</span>
                  <span className="font-mono font-bold">${ttm.toFixed(2)}</span>
                  {pendingPct > 0.5 && (
                    <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400">
                      (+{pendingPct.toFixed(1)}% raise pending)
                    </span>
                  )}
                </div>
              )}
              {showProjection && (
                <div
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-dashed border-muted-foreground/30 bg-muted/20"
                  title={`CAGR-projected annual dividend income. Each position compounds at its own 3y / 5y dividend growth rate. Weighted CAGR: ${wc3.toFixed(2)}% / ${wc5.toFixed(2)}%. Coverage: ${cagrCov.toFixed(0)}% of your portfolio's income has growth-rate data available.`}
                  data-testid="summary-projection-pill"
                >
                  <span className="text-muted-foreground">Year-3</span>
                  <span className="font-mono font-bold text-violet-600 dark:text-violet-400">${y3.toFixed(2)}</span>
                  <span className="text-muted-foreground/70">·</span>
                  <span className="text-muted-foreground">Year-5</span>
                  <span className="font-mono font-bold text-violet-600 dark:text-violet-400">${y5.toFixed(2)}</span>
                  <span className="text-muted-foreground/70">·</span>
                  <span className="text-muted-foreground">@</span>
                  <span className="font-mono">{wc5.toFixed(1)}%/yr</span>
                  {cagrCov < 95 && (
                    <span className="text-muted-foreground/70">
                      ({cagrCov.toFixed(0)}% coverage)
                    </span>
                  )}
                </div>
              )}
              {/* Round P111o (Feb 2026) — Desktop Alerts + Tool
                  Box moved to the top row (inline with the Portfolio
                  selector). This slot stays hidden on desktop; the
                  mobile launcher rows above still own the <sm copies
                  (mobile Tool Box row + mobile search-bar area). */}
              <div className="hidden" data-testid="summary-fidelity-strip-actions-relocated">
                <button
                  type="button"
                  onClick={() => setAlertSettingsOpen((v) => !v)}
                  className={`hidden sm:inline-flex shrink-0 items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all active:scale-95 ${
                    alertSettingsOpen
                      ? 'border-violet-500 bg-violet-600 text-white hover:bg-violet-700'
                      : 'border-violet-300/60 dark:border-violet-700/60 bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/30 hover:from-violet-100 hover:to-purple-100 dark:hover:from-violet-900/40 dark:hover:to-purple-900/40 text-violet-800 dark:text-violet-100'
                  }`}
                  data-testid="summary-alerts-settings-toggle"
                  title="Configure ex-dividend alert pop-ups"
                >
                  {/* Round 98p (Jul 2026) — size-matched to the
                      ToolBox button on the right (px-3 py-1.5,
                      rounded-lg, 22px icon, gap-2) so the two pills
                      look like a paired set instead of "tiny +
                      big". Watchlist / Calendar Alerts buttons stay
                      the smaller px-2.5 py-1 style per user spec. */}
                  <BellRing className="w-[22px] h-[22px]" />
                  Alerts
                </button>
                {ToolBoxBtn}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Round 98o (Jul 2026) — Ex-Div Alert settings disclosure panel
          MOVED here (was previously after <MacroYieldStrip>). Now
          renders DIRECTLY BELOW the "Alerts" toolbar button, pushing
          down the US Treasury / Portfolio vs 10Y / Real Yield row.
          Matches the disclosure pattern used on the Calendar and IPO
          Watch pages. */}
      {alertSettingsOpen && (
        <div className="mt-3 mx-2 sm:mx-3 rounded-xl border-2 border-violet-500/30 bg-card p-4" data-testid="summary-alerts-settings-panel">
          <ExDivAlertSettings
            onClose={() => setAlertSettingsOpen(false)}
            onStockClick={(ticker) => { setAlertSettingsOpen(false); onStockClick?.(ticker); }}
          />
        </div>
      )}

      {/* Phase 6b (Jun 2026) — Live U.S. Treasury yield curve strip
          + portfolio-vs-10Y spread pill. Sits directly under the
          Forward/Last-365d/Year-3/Year-5 income forecast row so users
          can see at a glance whether their dividend portfolio is
          earning a meaningful spread over the risk-free rate. Hides
          itself silently when the macro_yields cache hasn't been
          populated yet (first deploy before nightly cron runs). */}
      <MacroYieldStrip portfolioYieldPct={Number(data.portfolio_yield) || null} />

      {/* Daily Stock Tracking — May 2026. Collapsible per-ticker
          holdings table with live-price math. Closed by default so
          most page loads don't pay the cost of the aggregation query;
          lazy-loads the first time the user opens it, then re-uses
          the cached rows on toggle. Live prices flow in from the
          parent's existing `usePriceStream` subscription so opening
          the table costs zero extra SSE connections. */}
      <DailyStockTracker livePrices={livePrices} refreshKey={bump} onStockClick={onStockClick} />

      {/* Monthly Income Goal moved to its own standalone card —
          rendered by `<IncomeGoalCard />` immediately under the Cash
          Balance card per user request (April 2026). */}

      <TransactionsModal
        open={showTransactions}
        onClose={() => setShowTransactions(false)}
        onCleared={() => window.dispatchEvent(new Event('portfolio-changed'))}
      />

      {/* Round 91 — Google / Excel Sheet download confirm modal.
          Stock-Modal-style red ❌ close button. Yes = download, No /
          ❌ / click-outside = cancel. */}
      <Dialog open={confirmDownloadOpen} onOpenChange={(o) => { if (!o) setConfirmDownloadOpen(false); }}>
        <DialogContent className="max-w-sm" data-testid="confirm-download-sheet-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 pr-8">
              <FileSpreadsheet className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              <span>Download Google / Excel Sheet?</span>
            </DialogTitle>
            <DialogDescription className="pt-2 text-sm leading-relaxed">
              This will download a pre-filled <span className="font-semibold">.xlsx</span> spreadsheet of your portfolio.
              You can open it in Excel or upload it to Google Drive and share the <span className="font-mono">/copy</span> link with friends.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setConfirmDownloadOpen(false)}
              data-testid="confirm-download-sheet-no"
            >No</Button>
            <Button
              onClick={() => { setConfirmDownloadOpen(false); downloadSheetTemplate(); }}
              data-testid="confirm-download-sheet-yes"
            >Yes, download</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Empty-portfolio guard modal — opens when the user clicks
          Share or Download Sheet with no positions. Tells them how to
          add stocks first instead of generating an empty share-page or
          empty .xlsx workbook. */}
      <Dialog open={emptyAction !== null} onOpenChange={(o) => { if (!o) setEmptyAction(null); }}>
        <DialogContent className="max-w-sm" data-testid="empty-portfolio-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              <span>Your portfolio is empty</span>
            </DialogTitle>
            <DialogDescription className="pt-2 text-sm leading-relaxed">
              {emptyAction === 'sheet'
                ? "Add at least one stock before downloading the Google/Excel sheet — without holdings the workbook has nothing to project."
                : "Add at least one stock before creating your share page — there's nothing to share yet."}
              {' '}
              In the <span className="font-semibold">All Stocks</span> tab, search for a ticker, click the row to open it, then use <span className="font-semibold">Add to Portfolio</span> in the <span className="font-semibold">My Portfolio</span> tab.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end pt-2">
            <Button onClick={() => setEmptyAction(null)} data-testid="empty-portfolio-close-btn">Got it</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </div>
  );
}

function TransactionsModal({ open, onClose, onCleared }) {
  const [txs, setTxs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortDir, setSortDir] = useState('desc'); // desc = newest first
  const [showEraseDialog, setShowEraseDialog] = useState(false);
  const [erasing, setErasing] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/portfolio/transactions`, { withCredentials: true }),
      axios.get(`${API}/portfolio/summary`, { withCredentials: true }),
    ])
      .then(([txRes, sumRes]) => {
        setTxs(txRes.data?.transactions || []);
        setSummary(sumRes.data || null);
      })
      .catch(() => { setTxs([]); setSummary(null); })
      .finally(() => setLoading(false));
  }, [open]);

  const sortedTxs = [...txs].sort((a, b) => {
    const ak = a.created_at || a.date || '';
    const bk = b.created_at || b.date || '';
    return sortDir === 'desc' ? bk.localeCompare(ak) : ak.localeCompare(bk);
  });

  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch { return iso; }
  };

  // P3 (Feb 2026): chip colour follows the cash-flow grammar set by
  // the Add Position cash-source buttons — outside money is emerald,
  // inside money is blue. So:
  //   • deposit / withdraw         → emerald (outside-boundary moves)
  //   • buy with cash_source=outside → emerald
  //   • buy with cash_source=inside  → blue
  //   • buy with cash_source=split   → emerald→blue gradient (matches
  //     the "Split" button on the Add Position form)
  //   • buy with cash_source=drip    → cyan (DRIP badge palette)
  //   • sell                          → blue (proceeds become inside cash)
  //   • remove                        → red (informational, no cash)
  const STATIC_TYPE_STYLE = {
    sell:     { bg: 'bg-blue-100 dark:bg-blue-900/40',     fg: 'text-blue-800 dark:text-blue-300',     label: 'SELL' },
    remove:   { bg: 'bg-red-100 dark:bg-red-900/40',       fg: 'text-red-800 dark:text-red-300',       label: 'REMOVE' },
    deposit:  { bg: 'bg-emerald-100 dark:bg-emerald-900/40', fg: 'text-emerald-800 dark:text-emerald-300', label: 'DEPOSIT' },
    withdraw: { bg: 'bg-emerald-100 dark:bg-emerald-900/40', fg: 'text-emerald-800 dark:text-emerald-300', label: 'WITHDRAW' },
  };

  const styleForTx = (tx) => {
    const t = (tx?.type || '').toLowerCase();
    if (t !== 'buy') return STATIC_TYPE_STYLE[t]
      || { bg: 'bg-muted', fg: 'text-foreground', label: (tx?.type || '?').toUpperCase() };
    // Buy variant — colour by cash source so the user can see at a
    // glance which lots were funded from fresh money vs portfolio
    // recycling. `is_drip` wins over `cash_source` because DRIP buys
    // are tagged with `cash_source="drip"` but their visual grammar
    // is the cyan water-drop palette, not emerald/blue.
    if (tx.is_drip || (tx.cash_source || '').toLowerCase() === 'drip') {
      return { bg: 'bg-cyan-100 dark:bg-cyan-900/40', fg: 'text-cyan-800 dark:text-cyan-300', label: 'BUY · DRIP' };
    }
    const src = (tx.cash_source || 'outside').toLowerCase();
    if (src === 'inside') {
      return { bg: 'bg-blue-100 dark:bg-blue-900/40', fg: 'text-blue-800 dark:text-blue-300', label: 'BUY · IN' };
    }
    if (src === 'split') {
      // Emerald→blue gradient, sharp 50% seam — mirrors the Split
      // button on the Add Position form. Uses a high-contrast white
      // foreground so the label stays readable across both halves.
      return {
        bg: 'bg-gradient-to-r from-emerald-100 from-50% to-blue-100 to-50% dark:from-emerald-900/40 dark:to-blue-900/40',
        fg: 'text-emerald-900 dark:text-emerald-200',
        label: 'BUY · SPLIT',
      };
    }
    return { bg: 'bg-emerald-100 dark:bg-emerald-900/40', fg: 'text-emerald-800 dark:text-emerald-300', label: 'BUY · OUT' };
  };

  const describe = (tx) => {
    const t = (tx.type || '').toLowerCase();
    if (t === 'buy') {
      // Suffix the buy line with the funding source so the row reads
      // self-explanatorily even after the chip (which only shows the
      // BUY · OUT/IN/SPLIT/DRIP shorthand) has been parsed.
      const isDrip = tx.is_drip || (tx.cash_source || '').toLowerCase() === 'drip';
      const src = (tx.cash_source || 'outside').toLowerCase();
      const suffix = isDrip
        ? ' · DRIP (no cash deducted)'
        : src === 'inside'
          ? ' · funded from portfolio cash'
          : src === 'split'
            ? ' · split (outside + portfolio cash)'
            : ' · funded outside';
      return `Bought ${tx.shares} ${tx.ticker} @ $${Number(tx.price || 0).toFixed(2)}${suffix}`;
    }
    if (t === 'sell') return `Sold ${tx.shares} ${tx.ticker} @ $${Number(tx.price || 0).toFixed(2)}`;
    if (t === 'remove') return `Removed ${tx.shares} ${tx.ticker} (no cash recorded)`;
    if (t === 'deposit') return `Added cash to portfolio${tx.note ? ` — ${tx.note}` : ''}`;
    if (t === 'withdraw') return `Withdrew cash${tx.note ? ` — ${tx.note}` : ''}`;
    return `${t} — ${tx.ticker || ''}`;
  };

  const toggleSort = () => setSortDir((d) => d === 'desc' ? 'asc' : 'desc');

  const eraseAll = async () => {
    if (erasing) return;
    setErasing(true);
    try {
      const { data } = await axios.post(`${API}/portfolio/transactions/clear`, {}, { withCredentials: true });
      const removed = data?.removed ?? 0;
      setTxs([]);
      setShowEraseDialog(false);
      onCleared?.();
      toast.success('Transactions erased', {
        description: removed === 1 ? '1 transaction removed.' : `${removed} transactions removed. Your positions and cash were untouched.`,
      });
    } catch {
      toast.error("Couldn't erase transactions", {
        description: 'Please try again in a moment.',
      });
    }
    setErasing(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      {/* Fixed-size modal: h-[80vh] on desktop, full-height on mobile. [&>button]:hidden
          hides the default tiny X so our custom red one is the only close button. */}
      <DialogContent
        className="p-0 overflow-hidden flex flex-col gap-0 h-[100dvh] w-screen max-w-none rounded-none left-0 top-0 translate-x-0 translate-y-0 sm:left-[50%] sm:top-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:h-[80vh] sm:w-full sm:max-w-2xl sm:rounded-lg [&>button]:hidden"
        data-testid="transactions-modal"
      >
        {/* Sticky top bar — never scrolls away */}
        <div className="border-b bg-card shrink-0">
          <div className="flex items-center gap-2 px-4 sm:px-6 py-3">
            <button
              onClick={toggleSort}
              className="flex items-center gap-2 min-w-0 flex-1 text-left hover:opacity-80 transition-opacity"
              data-testid="transactions-sort-toggle"
              title={sortDir === 'desc' ? 'Sorted newest first — click to flip' : 'Sorted oldest first — click to flip'}
            >
              <ScrollText className="w-5 h-5 text-violet-500 shrink-0" />
              <span className="text-base font-bold truncate">All Transactions</span>
              <ArrowUpDown
                className={`w-4 h-4 text-muted-foreground transition-transform ${sortDir === 'asc' ? 'rotate-180' : ''}`}
              />
            </button>
            <button
              onClick={onClose}
              className="shrink-0 w-9 h-9 rounded-md flex items-center justify-center bg-red-500 hover:bg-red-600 text-white transition-colors"
              data-testid="transactions-close-btn"
              aria-label="Close"
            >
              <X className="w-5 h-5" strokeWidth={3} />
            </button>
          </div>
          {/* Stocks / Cash / Total summary row */}
          {summary && (
            <div className="flex items-center justify-center gap-3 sm:gap-6 flex-wrap px-3 pb-2.5 text-[11px] sm:text-xs" data-testid="transactions-summary-strip">
              <span>
                <span className="font-semibold text-muted-foreground">Stocks:</span>{' '}
                <span className="font-mono font-bold">${Number(summary.positions_value ?? ((summary.total_value || 0) - (summary.cash_balance || 0))).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </span>
              <span>
                <span className="font-semibold text-muted-foreground">Cash:</span>{' '}
                <span className="font-mono font-bold text-emerald-700 dark:text-emerald-400">${Number(summary.cash_balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </span>
              <span>
                <span className="font-semibold text-muted-foreground">Total:</span>{' '}
                <span className="font-mono font-bold text-blue-700 dark:text-blue-400">${Number(summary.total_value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </span>
            </div>
          )}
        </div>

        {/* Scrollable list body */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4" data-testid="transactions-list">
          {loading ? (
            <div className="py-16 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : sortedTxs.length === 0 ? (
            <div className="py-16 text-center" data-testid="transactions-empty">
              <ScrollText className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-60" />
              <p className="text-sm text-muted-foreground">No transactions yet.</p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                Buy/sell shares, add cash, or withdraw to build your log.
              </p>
            </div>
          ) : (
            <ul className="space-y-1.5">
              {sortedTxs.map((tx) => {
                const st = styleForTx(tx);
                const amt = Number(tx.amount || 0);
                const showAmt = (tx.type || '').toLowerCase() !== 'remove';
                return (
                  <li
                    key={tx.tx_id || tx.created_at}
                    className="flex items-center justify-between gap-3 rounded-lg border bg-card px-3 py-2"
                    data-testid={`transaction-${tx.type}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`shrink-0 inline-flex items-center justify-center min-w-[80px] px-2 py-0.5 rounded-full text-[10px] font-bold ${st.bg} ${st.fg}`}>
                        {st.label}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate">{describe(tx)}</p>
                        <p className="text-[10px] text-muted-foreground">{fmtDate(tx.created_at)}</p>
                      </div>
                    </div>
                    {showAmt && (
                      <span className={`shrink-0 text-sm font-mono font-bold ${st.fg}`}>
                        ${amt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Sticky footer — Erase All Transactions. Lives outside the
            scroll container so it stays pinned regardless of how many
            transactions are loaded. Only renders when there's something
            to erase. */}
        {!loading && sortedTxs.length > 0 && (
          <div className="shrink-0 border-t bg-card px-4 sm:px-6 py-3" data-testid="transactions-footer">
            <button
              onClick={() => setShowEraseDialog(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-red-500/70 bg-red-50/70 dark:bg-red-950/30 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-950/50 transition-colors text-sm font-bold"
              data-testid="erase-all-transactions-btn"
            >
              <AlertTriangle className="w-5 h-5" />
              Erase All Transactions List
            </button>
          </div>
        )}
      </DialogContent>

      {/* Confirmation dialog */}
      <Dialog open={showEraseDialog} onOpenChange={(o) => !o && setShowEraseDialog(false)}>
        <DialogContent className="sm:max-w-md" data-testid="erase-transactions-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700 dark:text-red-400">
              <AlertTriangle className="w-5 h-5" />
              Erase All Transactions?
            </DialogTitle>
            <DialogDescription className="space-y-2 pt-1">
              <span className="block">
                This will permanently delete your entire transaction history
                ({sortedTxs.length} {sortedTxs.length === 1 ? 'entry' : 'entries'}):
                every buy, sell, remove, deposit, and withdraw you've ever logged.
              </span>
              <span className="block font-semibold text-red-700 dark:text-red-400">
                Your stock positions and cash balance will be untouched — but the
                historical log for how you got there will be gone.
              </span>
              <span className="block font-bold">
                This action cannot be undone.
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setShowEraseDialog(false)} disabled={erasing} data-testid="erase-transactions-cancel">
              Cancel
            </Button>
            <Button variant="destructive" onClick={eraseAll} disabled={erasing} data-testid="erase-transactions-confirm">
              {erasing ? 'Erasing…' : `Yes, erase all ${sortedTxs.length}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

function BenchmarkCard({ name, ticker, returnPct, periodLabel, series, onClick }) {
  const isPositive = returnPct >= 0;
  // Wrap the benchmark name with InfoTip when we have a definition for
  // it (SCHD, VYM, HDV, NOBL). InfoTip auto-clamps to viewport so the
  // popover never falls off-screen on phones. The "MY PORTFOLIO" card
  // has no ticker and no definition, so it renders unchanged.
  const info = ticker && BENCHMARK_INFO[ticker];
  // MY PORTFOLIO tile is a button (same affordance as the Net Capital
  // Gain tile): coloured border by sign + "Click or Tap" hint, opens
  // the beat/trail breakdown modal.
  const clickable = typeof onClick === 'function';
  const Wrapper = clickable ? 'button' : 'div';
  const borderTone = !clickable
    ? 'border'
    : isPositive
      ? 'border-2 border-green-400 dark:border-green-700 hover:bg-green-50/60 dark:hover:bg-green-950/30 focus-visible:ring-green-500/40 cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-2'
      : 'border-2 border-red-400 dark:border-red-700 hover:bg-red-50/60 dark:hover:bg-red-950/30 focus-visible:ring-red-500/40 cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-2';
  return (
    <Wrapper
      {...(clickable ? { type: 'button', onClick, title: 'See how many percentage points you are beating or trailing each benchmark by' } : {})}
      className={`rounded-lg bg-card p-3 text-center flex flex-col items-center justify-center min-w-0 ${borderTone}`}
      data-testid={`benchmark-${ticker || 'portfolio'}`}
    >
      {info ? (
        <InfoTip
          term={info.term}
          def={info.def}
          className="!no-underline !decoration-transparent text-xs font-extrabold text-foreground uppercase tracking-wider mb-1"
          iconSize={10}
          maxWidth={280}
          testId={`benchmark-tip-${ticker}`}
        >
          {name}
        </InfoTip>
      ) : (
        <p className="text-xs font-extrabold text-foreground uppercase tracking-wider mb-1">{name}</p>
      )}
      {ticker && !info && <p className="text-xs font-bold text-foreground mb-1">({ticker})</p>}
      <p className={`text-lg font-bold font-mono flex items-center justify-center gap-0.5 ${isPositive ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
        {isPositive ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
        {Math.abs(returnPct)}%
      </p>
      <BenchmarkSparkline series={series} positive={isPositive} testId={`benchmark-sparkline-${ticker || 'portfolio'}`} />
      <p className="text-xs font-bold text-foreground mt-1">{periodLabel}</p>
      {clickable && (
        <p className="text-[10px] text-violet-500 dark:text-violet-400 mt-0.5" data-testid="benchmark-portfolio-hint">
          Click or Tap for Breakdown
        </p>
      )}
    </Wrapper>
  );
}

function BenchmarkComparison({ onCompareClick }) {
  const [period, setPeriod] = useState('3m');
  // Round 84k Phase 5.2 — toggle which benchmark lineup to show.
  // 'dividend' = SCHD/VYM/HDV/NOBL (default)
  // 'index'    = SPY/QQQ/DIA/IWM
  const [benchSet, setBenchSet] = useState('dividend');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [beatModalOpen, setBeatModalOpen] = useState(false);
  // Round 92o++++++++N++++++++++ (Jun 2026) — Plus multi-portfolio.
  // Re-fetch when the active portfolio changes so "My Portfolio"
  // return reflects ONLY that group's lots. Previously the card
  // showed the same number on every portfolio view.
  const { activeId: activePortfolioId } = useActivePortfolioId();
  const scopeKey = activePortfolioId || 'all';

  const swrTick = useSwrTick('/portfolio/benchmark');
  useEffect(() => {
    setLoading(true);
    const params = { period, bench_set: benchSet };
    if (activePortfolioId) params.portfolio_id = activePortfolioId;
    axios.get(`${API}/portfolio/benchmark`, { params, withCredentials: true })
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [period, benchSet, scopeKey, swrTick]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="rounded-lg border bg-card p-4 mb-3" data-testid="benchmark-comparison">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="text-base font-extrabold flex items-center gap-2">
          <Trophy className="w-4 h-4 text-amber-500 dark:text-amber-400 shrink-0" />
          <span>Benchmark Comparison</span>
        </h3>
        {/* Compare button — May 2026: relocated from the page-wide
            action bar to live centered between this title and the
            period selector, per user request. Only renders when the
            parent provides a click handler so the standalone use of
            BenchmarkComparison (if any) keeps its original two-cell row. */}
        {onCompareClick && (
          <div className="flex-1 flex justify-center min-w-0">
            <PortfolioActionLink
              icon={GitCompare}
              label="Compare"
              onClick={onCompareClick}
              tone="text-fuchsia-500 dark:text-fuchsia-400"
              testId="portfolio-compare-btn"
              title="Compare any two stocks or ETFs side by side — yields, growth, risk, total return"
              sizeBoost={2}
              bordered
            />
          </div>
        )}
        <div className="flex gap-0.5" data-testid="benchmark-period-buttons">
          {PERIODS.map(p => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              data-testid={`benchmark-period-${p.key}`}
              className={`px-2 py-1 text-[10px] font-bold rounded-md transition-colors ${
                period === p.key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >{p.label}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-24">
          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* 6-up grid matching the Portfolio summary box at the top
              of the page: 2 cols on mobile, 3 on md, 6 on lg+. The
              slider widget is the 6th cell so it sits inline with the
              benchmark cards instead of below them. */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="benchmark-cards">
            {data?.benchmarks?.map((b, i) => (
              <BenchmarkCard
                key={i}
                name={b.name}
                ticker={b.ticker}
                returnPct={b.return_pct}
                periodLabel={b.period_label}
                series={b.series}
                onClick={!b.ticker ? () => setBeatModalOpen(true) : undefined}
              />
            ))}
            <BenchmarkBeatModal
              open={beatModalOpen}
              onClose={() => setBeatModalOpen(false)}
              benchmarks={data?.benchmarks || []}
              benchSet={benchSet}
              info={BENCHMARK_INFO}
            />

            {/* 6th cell: benchmark set toggle.
                Mental model (per user, Feb 2026):
                  · Dividend mode → ball on RIGHT, arrow LEFT, "Switch to Index"
                  · Index mode    → ball on LEFT,  arrow RIGHT, "Switch to Dividend"
                Arrow always points where the ball will travel. */}
            <div
              className="rounded-lg border border-violet-200 dark:border-violet-800/60 bg-violet-50/50 dark:bg-violet-950/20 p-3 flex flex-col items-center justify-center gap-1.5 min-w-0"
              data-testid="benchmark-set-toggle-wrap"
            >
              <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                Showing
              </span>
              <span className="text-xs font-extrabold text-violet-700 dark:text-violet-300 whitespace-nowrap text-center">
                {benchSet === 'dividend' ? 'Dividend' : 'Index'} Benchmarks
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={benchSet === 'dividend'}
                aria-label={`Switch to ${benchSet === 'dividend' ? 'Index' : 'Dividend'} Benchmarks`}
                onClick={() => setBenchSet((v) => (v === 'dividend' ? 'index' : 'dividend'))}
                data-testid="benchmark-set-toggle"
                className="relative w-14 h-7 rounded-full bg-violet-500 dark:bg-violet-600 transition-colors shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-2 shadow-inner"
              >
                <span
                  className={`absolute top-0.5 left-0 w-6 h-6 rounded-full bg-white shadow-md transition-transform ${
                    benchSet === 'dividend' ? 'translate-x-[30px]' : 'translate-x-0.5'
                  }`}
                />
              </button>
              <button
                type="button"
                onClick={() => setBenchSet((v) => (v === 'dividend' ? 'index' : 'dividend'))}
                data-testid="benchmark-set-switch-hint"
                className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground hover:text-violet-700 dark:hover:text-violet-300 transition-colors"
              >
                {benchSet === 'dividend' ? (
                  <>
                    <ArrowLeft className="w-3 h-3" />
                    <span>Switch to Index</span>
                  </>
                ) : (
                  <>
                    <span>Switch to Dividend</span>
                    <ArrowRight className="w-3 h-3" />
                  </>
                )}
              </button>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground/60 mt-3">
            Portfolio return is price-only (excludes dividend income).
          </p>
        </>
      )}
    </div>
  );
}

export default function PortfolioSummary({ isDark, refreshKey, onWhatIfClick, onCompareClick, onStockClick, onRebalancerClick, onTaxLotsClick, onYearEndClick, onTaxAuditClick, onExportCsv, onImportCsv, onDripCalcClick, backSlot }) {
  // Round P111o (Feb 2026) — Alert settings open/close state for the
  // top-row Alerts button (desktop-only). Kept local to
  // PortfolioSummary so the disclosure panel can render ABOVE the
  // SummaryCard when open. SummaryCard has its own copy of this
  // state for the (now-hidden) fidelity-strip button + mobile UI;
  // both stay independently valid.
  const [alertSettingsOpen, setAlertSettingsOpen] = useState(false);
  return (
    <div data-testid="portfolio-summary-section">
      {/* Round 92o++++++++N++++++++++ (May 2026) — Plus multi-portfolio
          selector. Lives at THIS level (sibling of `SummaryCard`)
          rather than INSIDE SummaryCard because SummaryCard is keyed
          on `refreshKey` and remounts on every `portfolio-changed`
          event. If the selector lived inside it, the Create
          Portfolio modal would get unmounted mid-flow (right after
          the user clicked "Create portfolio") — the user reported
          exactly this bug: the choose-how step flashed for a moment
          then disappeared. Lifting the selector out keeps the modal
          alive across refreshes. */}
      {/* Round P111o (Feb 2026) — Desktop-only inline row hosting
          the Back button (LEFT), the PortfolioSelector chip
          (CENTER, fills), and the Alerts + Tool Box launchers
          (RIGHT). All three groups only render at ≥sm — mobile
          keeps its own copies (Back on Dashboard mobile toolbar,
          Alerts + Tool Box in the mobile launcher rows below). */}
      <div className="flex items-start gap-3">
        {backSlot && (
          <div
            className="hidden sm:block shrink-0 pt-2"
            data-testid="portfolio-back-inline-desktop"
          >
            {backSlot}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <PortfolioSelector />
        </div>
        <div
          className="hidden sm:flex shrink-0 items-center gap-2 pt-2"
          data-testid="portfolio-top-row-actions-desktop"
        >
          <button
            type="button"
            onClick={() => setAlertSettingsOpen((v) => !v)}
            className={`inline-flex shrink-0 items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all active:scale-95 ${
              alertSettingsOpen
                ? 'border-violet-500 bg-violet-600 text-white hover:bg-violet-700'
                : 'border-violet-300/60 dark:border-violet-700/60 bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/30 hover:from-violet-100 hover:to-purple-100 dark:hover:from-violet-900/40 dark:hover:to-purple-900/40 text-violet-800 dark:text-violet-100'
            }`}
            data-testid="summary-alerts-settings-toggle-top"
            title="Configure ex-dividend alert pop-ups"
          >
            <BellRing className="w-[22px] h-[22px]" />
            Alerts
          </button>
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event('open-toolbox-modal'))}
            data-testid="toolbox-launcher-desktop-top"
            className="inline-flex shrink-0 items-center gap-2 px-3 py-1.5 rounded-lg border border-fuchsia-300/60 dark:border-amber-200/40 bg-gradient-to-r from-fuchsia-50 to-amber-50 dark:from-fuchsia-950/30 dark:to-amber-950/30 hover:from-fuchsia-100 hover:to-amber-100 dark:hover:from-fuchsia-900/40 dark:hover:to-amber-900/40 text-xs font-semibold transition-all active:scale-95"
            title="Open the Tool Box — every portfolio tool in one place"
          >
            <img src="/icons/toolbox.png" alt="" className="w-[22px] h-[22px] object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            <span className="text-fuchsia-800 dark:text-amber-100">Tool Box</span>
          </button>
        </div>
      </div>
      {/* Round P111o (Feb 2026) — Ex-Div Alert settings disclosure
          panel for the TOP-ROW Alerts button. Renders ABOVE the
          SummaryCard, pushing it down as the user requested. Only
          visible on desktop (mobile keeps its own alert panel
          inside SummaryCard). */}
      {alertSettingsOpen && (
        <div
          className="hidden sm:block mt-3 mb-6 mx-2 sm:mx-3 rounded-xl border-2 border-violet-500/30 bg-card p-4"
          data-testid="portfolio-top-alerts-settings-panel"
        >
          <ExDivAlertSettings
            onClose={() => setAlertSettingsOpen(false)}
            onStockClick={(ticker) => { setAlertSettingsOpen(false); onStockClick?.(ticker); }}
          />
        </div>
      )}
      <SummaryCard isDark={isDark} key={`sum-${refreshKey}`} onWhatIfClick={onWhatIfClick} onStockClick={onStockClick} />
      <CapitalTrackerCard
        refreshKey={refreshKey}
        onTaxLotsClick={onTaxLotsClick}
        onYearEndClick={onYearEndClick}
        onTaxAuditClick={onTaxAuditClick}
        onExportCsv={onExportCsv}
        onImportCsv={onImportCsv}
      />
      {/* Cash Balance now lives INSIDE the Capital Tracker card. */}
      {/* Round 86 (Feb 2026) — Concentration risk banner relocated
          here from PortfolioView so the warning sits in the
          eye-catching summary block rather than buried above the
          positions list. Self-hides for balanced portfolios.
          Round 93o (Jun 2026) — banner MOVED into each portfolio
          section of the Income by Portfolio card. This global
          mount now hides itself for users whose contrast card is
          rendering per-portfolio banners (Plus users with one or
          more named portfolios). Free users keep the global
          banner because their /portfolios/contrast returns []. */}
      <IncomeConcentrationBanner onStockClick={onStockClick} refreshKey={refreshKey} hideWhenContrastShowsBanners />
      {/* Round 86 (Feb 2026) — Portfolio Value chart + Sector Allocation
          doughnut moved here so they render directly under the Capital
          Tracker box (per user request — visual "results" grouping
          alongside the cash-flow ledger). */}
      <PortfolioCharts
        isDark={isDark}
        refreshKey={refreshKey}
        onStockClick={onStockClick}
        onRebalancerClick={onRebalancerClick}
      />
      <IncomeGoalCard key={`goal-${refreshKey}`} onDripCalcClick={onDripCalcClick} />
    </div>
  );
}

// Re-export so Dashboard.js can render it after the Sector Allocation
// charts (per April-2026 layout request: "Benchmark Comparison should sit
// under the Portfolio Value / Sector Allocation block").
export { BenchmarkComparison };

// Standalone Income Goal card — extracted from SummaryCard so it can
// render between CashCard and the charts grid. Owns its own goal fetch
// + monthly-dividends fetch + GoalModal state. Listens to the same
// portfolio-changed / cash-changed events as the other summary cards
// so edits anywhere refresh the displayed monthly income.
function IncomeGoalCard({ onDripCalcClick }) {
  const [goal, setGoal] = useState({ monthly_goal: 0, growth_pct: 8 });
  const [monthlyIncome, setMonthlyIncome] = useState(0);
  const [portfolioYield, setPortfolioYield] = useState(0);
  const [editingGoal, setEditingGoal] = useState(false);
  const [bump, setBump] = useState(0);
  const { activeId: activePortfolioId } = useActivePortfolioId();
  // Round 92o++++++++N++++++++++ (Jun 2026) — Plus tier All view is
  // read-only summary; editing the goal there is meaningless because
  // the goal is per-user, not per-portfolio. Hide the title button +
  // pencil edit affordance and show a banner.
  const { isReadOnly: isAllReadOnly } = useIsReadOnlyAllView();

  const swrTick = useSwrTick('/portfolio/summary');
  useEffect(() => {
    const params = activePortfolioId ? { portfolio_id: activePortfolioId } : {};
    axios.get(`${API}/portfolio/income-goal`, { withCredentials: true })
      .then(res => setGoal(res.data))
      .catch(() => {});
    dedupedGet(`${API}/portfolio/headline`, { params, withCredentials: true })
      .then(res => {
        setMonthlyIncome(Number(res.data?.monthly_dividends || 0));
        setPortfolioYield(Number(res.data?.portfolio_yield || 0));
      })
      .catch(() => {});
  }, [bump, activePortfolioId, swrTick]);

  useEffect(() => {
    const h = () => setBump((b) => b + 1);
    window.addEventListener('cash-changed', h);
    window.addEventListener('portfolio-changed', h);
    return () => {
      window.removeEventListener('cash-changed', h);
      window.removeEventListener('portfolio-changed', h);
    };
  }, []);

  return (
    <div className="rounded-lg border bg-card p-4 mb-3" data-testid="income-goal-card">
      {/* Title row is now a button — clicking the icon or the
          "Monthly Dividend Goal" text opens the same edit modal as the
          inline "Set Goal" / pencil-edit affordances. Hover affordance
          (background tint) tells the user it's interactive. On the
          Plus-tier All view the title becomes a plain (non-clickable)
          label so users can't accidentally try to edit goals from a
          summary view.

          Round 95bp (Jun 2026) — DRIP Calculator launcher was moved
          here from `PersonalStreaksCard` per user feedback ("if someone
          is setting a dividend goal, they would want to check the DRIP
          calculator first"). Sits at the top-right of the card so it
          doesn't compete visually with the goal-edit affordance. */}
      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
        {isAllReadOnly ? (
          <div className="flex items-center gap-2 -mx-1 px-1" data-testid="income-goal-title-readonly">
            <div className="w-8 h-8 rounded-lg bg-violet-500/10 flex items-center justify-center">
              <Target className="w-4 h-4 text-violet-500" />
            </div>
            <h2 className="text-base font-bold">Monthly Dividend Goal</h2>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setEditingGoal(true)}
            className="flex items-center gap-2 -mx-1 px-1 rounded-md hover:bg-muted/40 transition-colors"
            data-testid="income-goal-title-btn"
            title="Click to set or edit your monthly dividend goal"
          >
            <div className="w-8 h-8 rounded-lg bg-violet-500/10 flex items-center justify-center">
              <Target className="w-4 h-4 text-violet-500" />
            </div>
            <h2 className="text-base font-bold">Monthly Dividend Goal</h2>
          </button>
        )}
        {onDripCalcClick && (
          <PortfolioActionLink
            icon={Calculator}
            label="DRIP Calculator"
            onClick={onDripCalcClick}
            tone="text-violet-500 dark:text-violet-400"
            testId="portfolio-drip-calc-btn"
            title="Project future portfolio value with dividend reinvestment compounded over time"
            sizeBoost={1}
            bordered
          />
        )}
      </div>
      <IncomeGoalBar
        goal={goal}
        monthlyIncome={monthlyIncome}
        portfolioYield={portfolioYield}
        onEditClick={isAllReadOnly ? null : () => setEditingGoal(true)}
      />
      {isAllReadOnly && (
        <div className="mt-2">
          <AllViewReadOnlyBanner verb="set or edit your goal" compact />
        </div>
      )}
      <GoalModal
        open={editingGoal}
        initial={goal}
        onClose={() => setEditingGoal(false)}
        onSaved={(g) => { setGoal(g); setEditingGoal(false); }}
      />
    </div>
  );
}

